/**
 * Programmatic "save this URL to disk" via a hidden <iframe>.
 *
 * Why an iframe and not `<a download>.click()`: the presigned bundle URL is
 * cross-origin (S3). Clicking a cross-origin `<a download>` makes the browser
 * treat it as a TOP-LEVEL NAVIGATION, which aborts any in-flight fetch fired in
 * the same tick — notably the best-effort `/complete` POST — surfacing as a
 * "NetworkError when attempting to fetch resource" TypeError in Firefox and
 * Safari (Chrome happened to tolerate it). Loading the URL in a hidden iframe
 * keeps the top document put: the presigned/single-file URLs carry
 * Content-Disposition: attachment, so the iframe load becomes a download
 * (streamed straight to disk, the browser's own progress UI, no in-memory
 * buffering — safe for very large bundles) while the page and its pending
 * requests are untouched. Same behaviour in Chrome, Firefox and Safari.
 *
 * No filename is passed: cross-origin downloads ignore a `download=""` attribute
 * and take their name from the URL's Content-Disposition header.
 *
 * Shared by every download entry point so the DOM dance lives in one place.
 */
const triggerAnchorDownload = (url: string): void => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';

  // Reclaim the (empty, hidden) iframe once the browser owns the transfer —
  // never so early that a slow/large download gets cut off (removing it
  // mid-flight aborts the transfer in Firefox).
  //
  // NB: `iframe.onload` does NOT fire for a Content-Disposition: attachment
  // response — the iframe never completes a load (MDN Content-Disposition;
  // whatwg/html#4965). So for the presigned bundle URLs the load handler only
  // covers a non-attachment edge case; the generous fallback is what actually
  // removes the node after a large bundle has had ample time to hand off to the
  // browser's own download manager (which then continues independently).
  const removeIframe = () => iframe.remove();
  const fallback = window.setTimeout(removeIframe, 10 * 60_000);
  iframe.onload = () => {
    window.clearTimeout(fallback);
    window.setTimeout(removeIframe, 5_000);
  };

  iframe.src = url;
  document.body.appendChild(iframe);
};

export default triggerAnchorDownload;
